import React, { useContext, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { AgendaContext } from '../../../contexts/AgendaContext.jsx';
// Mui components
import { Box, Typography, Button, Tooltip } from '@mui/material';
// Components
import HorizontalDivider from '../../common/HorizontalDivider.jsx';
import ChipEstadoSolicitud from '../../Home/SolicitudCard/ChipEstadoSolicitud.jsx';
import ModalSolicitarBaja from '../Modals/ModalSolicitarBaja.jsx';
// Hooks
import { toCamelCaseAllWords, toCamelCaseFirstWord } from '../../../utils/Hooks/textFormatted.js';
// Icons
import IconCircleInformation from '../../../utils/images/icon_circle-information.svg';
import IconClock from '../../../utils/images/icon_clock.svg';

function SeccionInfoConsultorio({consultorio}) {
  const theme = useTheme();
  const { consultorioId } = useParams();
  const navigate = useNavigate();
  const { consultorioDetail, solicitudesPrestador } = useContext(AgendaContext);

  const [openModalSolicitarBaja, SetOpenModalSolicitarBaja] = useState(false);

  const handleOpenModalSolicitarBaja = () => {
    SetOpenModalSolicitarBaja(!openModalSolicitarBaja);
  };

  const direccionOsdeArray = consultorioDetail?.consultorioOsde[0]?.direccion.split(',');
  const direccionOsdeFormateada = [...new Set(direccionOsdeArray)].join(', ');

  const telefonos = consultorioDetail?.contactos?.filter((contacto) => contacto.system === 'phone');
  const emails = consultorioDetail?.contactos?.filter((contacto) => contacto.system === 'email');

  const solicitudesModificacion = solicitudesPrestador.find(
    (solicitud) =>
      solicitud.tipoSolicitud === 'MODIFICAR_CONSULTORIO' &&
      solicitud.consultorioOsde === consultorioDetail?.consultorioOsde[0].consultorioId &&
      solicitud.status === 'PENDIENTE'
  );

  const solicitudesBaja = solicitudesPrestador.find(
    (solicitud) =>
      solicitud.tipoSolicitud === 'BAJA_CONSULTORIO' &&
      solicitud.consultorioOsde === consultorioDetail?.consultorioOsde[0].consultorioId &&
      solicitud.status === 'PENDIENTE'
  );

  return (
    consultorioDetail && (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: 'auto',
          border: `2px solid ${theme.palette.backgroundSecondary.main}`,
          borderRadius: '1rem',
          gap: '1.5rem',
          padding: '1.5rem',
          flexDirection: 'column'
        }}>
        <ModalSolicitarBaja
          modalIsVisible={openModalSolicitarBaja}
          handleClose={handleOpenModalSolicitarBaja}
          consultorio={consultorio}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h4">Detalle del consultorio</Typography>
          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {solicitudesModificacion && (
              <Tooltip
                title="Este consultorio tiene una solicitud de modificación."
                placement="top">
                <Box
                  component="img"
                  sx={{ width: '25px', height: '25px', cursor: 'pointer' }}
                  alt="img not found"
                  src={IconClock}
                />
              </Tooltip>
            )}
            {solicitudesBaja && (
              <Tooltip title="Este consultorio tiene una solicitud de desconexión." placement="top">
                <Box
                  component="img"
                  sx={{ width: '25px', height: '25px', cursor: 'pointer' }}
                  alt="img not found"
                  src={IconClock}
                />
              </Tooltip>
            )}
            {consultorioDetail.status === 'INTEROPERANDO' ? (
              <ChipEstadoSolicitud
                backGroundColor={theme.palette.successBackground.main}
                text="Conectado"
                colorText={theme.palette.successSecondary.main}
              />
            ) : consultorioDetail.status === 'PENDIENTE' ? (
              <ChipEstadoSolicitud
                backGroundColor={theme.palette.backgroundSecondary.main}
                text="Conexión solicitada"
                colorText={theme.palette.primary.main}
              />
            ) : consultorioDetail.status === 'BAJA' ? (
              <ChipEstadoSolicitud
                backGroundColor={theme.palette.graySuperLight.main}
                text="Desconectado"
                colorText={theme.palette.black.main}
              />
            ) : null}
          </Box>
        </Box>
        {solicitudesBaja && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
            <Box
              component="img"
              sx={{ width: '16px', height: '16px' }}
              alt="img not found"
              src={IconCircleInformation}
            />
            <Typography variant="bodyS">Tenés una solicitud de desconexión.</Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: '1.5rem' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '424px' }}>
            <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
              Dirección del consultorio
            </Typography>
            <Typography variant="h5">{direccionOsdeFormateada}</Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
              Proveedor
            </Typography>
            <Typography variant="h5">{toCamelCaseAllWords(consultorioDetail.proveedor)}</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
            Consultorio
          </Typography>
          <Typography variant="h5">{consultorioId}</Typography>
        </Box>

        <HorizontalDivider />
        {consultorioDetail.status === 'INTEROPERANDO' && (
          <>
            <Typography variant="h4">Especialidades publicadas</Typography>

            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
              {consultorioDetail?.especialidades &&
                consultorioDetail?.especialidades.map((especialidad, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: '0.5rem' }}>
                    <Typography component="span" variant="h5">
                      {toCamelCaseFirstWord(especialidad.descripcion)}
                    </Typography>
                    <Typography component="span" variant="h5">
                      {index < consultorioDetail?.especialidades.length - 1 && ' | '}
                    </Typography>
                  </Box>
                ))}
            </Box>
            {solicitudesModificacion && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                <Box
                  component="img"
                  sx={{ width: '16px', height: '16px' }}
                  alt="img not found"
                  src={IconCircleInformation}
                />
                <Typography variant="bodyS">
                  Tenes una solicitud de modificación, no podrás realizar modificaciones hasta que
                  la misma sea resuelta.
                </Typography>
              </Box>
            )}

            <HorizontalDivider />
          </>
        )}

        <Typography variant="h4">Datos de contacto</Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
            Teléfonos
          </Typography>
          <Box sx={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', wordBreak: 'break-word' }}>
            {telefonos &&
              telefonos.map((telefono, index) => (
                <Box key={index} sx={{ display: 'flex', gap: '0.5rem' }}>
                  <Typography component="span" variant="h5">
                    {telefono.value}
                  </Typography>
                  <Typography component="span" variant="h5">
                    {index < telefonos.length - 1 && ' | '}
                  </Typography>
                </Box>
              ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
            Correos electrónicos
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              wordBreak: 'break-word'
            }}>
            {emails &&
              emails.map((email, index) => (
                <Box key={index} sx={{ display: 'flex', gap: '0.5rem' }}>
                  <Typography component="span" variant="h5">
                    {email.value}
                  </Typography>
                  <Typography component="span" variant="h5">
                    {index < emails.length - 1 && ' | '}
                  </Typography>
                </Box>
              ))}
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            marginTop: '1.5rem',
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
          <Button
            variant="text"
            disableRipple
            onClick={() =>
              navigate(`/prestador/misConsultorios/${consultorioId}/solicitarModificacion`)
            }
            disabled={
              consultorioDetail.status === 'PENDIENTE' ||
              consultorioDetail.status === 'BAJA' ||
              solicitudesModificacion ||
              solicitudesBaja
            }
            sx={{
              display: 'flex',
              padding: '0px 1rem 0px 0px',
              borderRight: `2px solid ${theme.palette.backgroundSecondary.main}`,
              borderRadius: '0px',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}>
            <Typography variant="textLink" sx={{ textDecoration: 'underline' }}>
              Editar especialidades
            </Typography>
          </Button>

          <Button
            variant="text"
            onClick={handleOpenModalSolicitarBaja}
            disableRipple
            disabled={
              consultorioDetail.status === 'PENDIENTE' ||
              consultorioDetail.status === 'BAJA' ||
              solicitudesBaja
            }
            sx={{
              color: theme.palette.primary.main,
              padding: '0px 0px 0px 1rem',
              display: 'flex',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}>
            <Typography variant="textLink" sx={{ textDecoration: 'underline' }}>
              Desconexión
            </Typography>
          </Button>
        </Box>
      </Box>
    )
  );
}

export default SeccionInfoConsultorio;
